import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sprout, Milk, GraduationCap, Handshake, Users, MapPin, Award, Leaf, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { SERVICES, PRODUCTS, STATS, PARTNERS, GALLERY } from "@/lib/content";

const iconMap = {
  sprout: Sprout,
  milk: Milk,
  "graduation-cap": GraduationCap,
  handshake: Handshake,
};

const statIcons = [Users, MapPin, GraduationCap, Award];

export default function HomePage() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="home-hero">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/29039798/pexels-photo-29039798.jpeg?auto=compress&cs=tinysrgb&w=2000"
            alt="Farmers in field"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 hero-gradient" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-36 lg:py-44">
          <div className="max-w-2xl text-white fade-in-up">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur border border-white/20 text-xs uppercase tracking-widest mb-6">
              <Leaf className="w-3.5 h-3.5" /> Farmer Producer Company
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] mb-6" style={{ fontFamily: "Outfit" }}>
              Empowering Farmers,<br />
              <span className="text-accent">Growing Together</span>
            </h1>
            <p className="text-base md:text-lg text-white/90 leading-relaxed mb-8 max-w-xl">
              Indapur Farmers Producer Company is committed to the development of farmers through quality inputs,
              modern technology, training and better market access.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button asChild size="lg" className="rounded-full bg-accent hover:bg-accent/90 text-white px-6" data-testid="hero-explore-btn">
                <Link to="/products">
                  Explore Our Services <ArrowRight className="w-4 h-4 ml-1.5" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="rounded-full bg-white/10 border-white text-white hover:bg-white hover:text-primary px-6" data-testid="hero-contact-btn">
                <Link to="/contact">Contact Us</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="relative -mt-12 md:-mt-16 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-primary text-white rounded-2xl shadow-xl px-6 md:px-10 py-8 md:py-10 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-4" data-testid="home-stats">
            {STATS.map((s, i) => {
              const Icon = statIcons[i];
              return (
                <div key={s.label} className="flex items-center gap-4 md:justify-center">
                  <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center shrink-0">
                    <Icon className="w-7 h-7" />
                  </div>
                  <div>
                    <div className="text-3xl md:text-4xl font-bold leading-none" style={{ fontFamily: "Outfit" }}>{s.value}</div>
                    <div className="text-sm text-white/80 mt-1">{s.label}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-20 md:py-28 leaf-pattern" data-testid="home-services">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14 max-w-2xl mx-auto">
            <span className="text-xs uppercase tracking-widest text-primary font-semibold">Our Services</span>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mt-2 mb-3" style={{ fontFamily: "Outfit" }}>
              End-to-end support for farmers
            </h2>
            <p className="text-muted-foreground">
              From quality inputs to market access, we are with farmers at every step of their journey.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {SERVICES.map((s) => {
              const Icon = iconMap[s.icon];
              return (
                <Card
                  key={s.key}
                  className="p-7 hover:-translate-y-1 hover:shadow-lg transition-all rounded-2xl border-border group"
                  data-testid={`service-card-${s.key}`}
                >
                  <div className="w-14 h-14 rounded-2xl bg-secondary text-primary flex items-center justify-center mb-5 group-hover:bg-primary group-hover:text-white transition-colors">
                    <Icon className="w-7 h-7" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: "Outfit" }}>{s.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{s.description}</p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCT (Indapur Agro Feed) */}
      <section className="py-20 md:py-28 bg-secondary/40" data-testid="home-featured-product">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <div className="absolute -inset-6 bg-gradient-to-br from-primary/10 to-accent/10 rounded-3xl rotate-3"></div>
            <img
              src={PRODUCTS[0].image}
              alt={PRODUCTS[0].name}
              className="relative rounded-3xl shadow-xl w-full object-cover bg-white p-4"
            />
          </div>
          <div>
            <span className="text-xs uppercase tracking-widest text-primary font-semibold">Featured Product</span>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mt-2 mb-4" style={{ fontFamily: "Outfit" }}>
              Indapur Agro Feed
            </h2>
            <p className="text-muted-foreground text-base leading-relaxed mb-6">
              High quality cattle feed for better milk production, animal health and farmer's prosperity. Nutritionally
              balanced and trusted by hundreds of dairy farmers across the region.
            </p>
            <ul className="space-y-3 mb-8">
              {[
                "Nutritionally balanced for higher milk yield",
                "Pure, hygienic and farmer-friendly pricing",
                "Available in convenient pack sizes",
                "Trusted by local dairy farmers",
              ].map((point) => (
                <li key={point} className="flex items-start gap-2 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                  <span>{point}</span>
                </li>
              ))}
            </ul>
            <div className="flex gap-3">
              <Button asChild className="rounded-full bg-primary hover:bg-primary/90" data-testid="feed-knowmore-btn">
                <Link to="/products">Know More <ArrowRight className="w-4 h-4 ml-1.5" /></Link>
              </Button>
              <Button asChild variant="outline" className="rounded-full border-primary text-primary hover:bg-secondary" data-testid="feed-enquire-btn">
                <Link to="/contact">Enquire Now</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ACTIVITIES PREVIEW */}
      <section className="py-20 md:py-28" data-testid="home-activities">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
            <div>
              <span className="text-xs uppercase tracking-widest text-primary font-semibold">Our Activities</span>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mt-2" style={{ fontFamily: "Outfit" }}>
                Glimpses of our work on the ground
              </h2>
            </div>
            <Button asChild variant="outline" className="rounded-full border-primary text-primary hover:bg-secondary w-fit" data-testid="activities-viewall-btn">
              <Link to="/activities">View All Activities <ArrowRight className="w-4 h-4 ml-1.5" /></Link>
            </Button>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {GALLERY.slice(0, 4).map((g, i) => (
              <div
                key={g.url}
                className="relative aspect-[4/5] rounded-2xl overflow-hidden group cursor-pointer"
                data-testid={`activity-img-${i}`}
              >
                <img src={g.url} alt={g.caption} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-primary/10 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                  <div className="text-sm font-medium">{g.caption}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNERS */}
      <section className="py-16 md:py-20 bg-secondary/40" data-testid="home-partners">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-xs uppercase tracking-widest text-primary font-semibold">Our Partners</span>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mt-2 mb-10" style={{ fontFamily: "Outfit" }}>
            Trusted collaborations
          </h2>
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
            {PARTNERS.map((p) => (
              <div key={p.name} className="bg-white rounded-2xl p-6 shadow-sm border border-border w-56 h-32 flex items-center justify-center" data-testid={`partner-${p.name.toLowerCase()}`}>
                <img src={p.image} alt={p.name} className="max-h-full max-w-full object-contain" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16" data-testid="home-cta">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-primary text-white rounded-3xl px-8 md:px-14 py-12 md:py-16 text-center relative overflow-hidden">
            <div className="absolute -top-10 -right-10 w-48 h-48 bg-accent/20 rounded-full blur-3xl" />
            <div className="absolute -bottom-10 -left-10 w-48 h-48 bg-white/10 rounded-full blur-3xl" />
            <div className="relative">
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: "Outfit" }}>
                Join the Indapur FPC family
              </h2>
              <p className="text-white/80 mb-8 max-w-2xl mx-auto">
                Register as a farmer member and unlock quality inputs, training opportunities and better markets for
                your produce.
              </p>
              <div className="flex flex-wrap gap-3 justify-center">
                <Button asChild size="lg" className="rounded-full bg-accent hover:bg-accent/90 text-white px-6" data-testid="cta-register-btn">
                  <Link to="/contact?form=register">Register as Farmer</Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="rounded-full bg-white/10 border-white text-white hover:bg-white hover:text-primary px-6" data-testid="cta-enquire-btn">
                  <Link to="/contact?form=enquiry">Product Enquiry</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
